import type { ISegmentExport } from '@feathr/blackbox';

/**
 * The visual mode for the Progress column cell.
 *
 * - `notApplicable`: render plain "N/A" text — used for stopped/failed rows.
 * - `calculating`: render "Calculating…" text only — the BE has not reported a
 *   `progress` percent yet.
 * - `complete`: render a full green bar at 100% — `state=ready` or `state=expired`
 *   (the export finished; only its download link aged out).
 * - `inProgress`: render a determinate bar at `percent`% — the BE reported a percent.
 */
export type TProgressMode =
  | { kind: 'notApplicable' }
  | { kind: 'calculating' }
  | { kind: 'complete' }
  | { kind: 'inProgress'; percent: number };

interface IProgressInputs {
  state: ISegmentExport['state'];
  /** Undefined when the API omits the field, e.g. on an export that never started. */
  progress: ISegmentExport['progress'] | undefined;
}

/**
 * Derives the visual mode for a row's Progress column from the BE `state` and
 * `progress` fields.
 *
 * Ordering matters: terminal-but-not-ready (`stopped`/`failed`) wins over the
 * percent, and `ready`/`expired` always show 100% even if `progress` lags behind.
 *
 * @param inputs - The relevant subset of `ISegmentExport` fields.
 * @returns The progress display mode.
 *
 * @example
 *   getProgressMode({ state: 'exporting', progress: 31 })
 *   // → { kind: 'inProgress', percent: 31 }
 */
export function getProgressMode({ state, progress }: IProgressInputs): TProgressMode {
  if (state === 'stopped' || state === 'failed') {
    return { kind: 'notApplicable' };
  }

  if (state === 'ready' || state === 'expired') {
    return { kind: 'complete' };
  }

  if (progress === null || progress === undefined) {
    return { kind: 'calculating' };
  }

  const percent = Math.max(0, Math.min(100, Math.round(progress)));
  return { kind: 'inProgress', percent };
}
